import React, { useState } from 'react';
import { Container, Box, Typography, Grid2, Paper, Button, Divider, Stack, Chip, Card, CardContent, useTheme, alpha } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowForward, CheckCircleOutline, Dashboard, Psychology, 
  School, SmartToy, NotificationsActive, HistoryEdu, AutoMode,
  CheckCircle
} from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { 
  admin_data, organization_data, primaryFeatures, 
  student_data_, tutor_data_, video_data_ 
} from './Helper';
import Footer from './Footer';
import AuditComplianceSection from './AuditComplianceSection';
import ValidationInsightsSection from './ValidationInsightSection';

// --- INTERNAL COMPONENTS ---

const FeatureSlide = ({ payload, reverse }) => (
  <Box sx={{ py: 6 }}>
    {payload.map((item, index) => (
      <Grid2 container spacing={6} alignItems="center" key={index} sx={{ mb: 8, flexDirection: (index % 2 !== 0 || reverse) ? 'row-reverse' : 'row' }}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <motion.div initial={{ opacity: 0, x: reverse ? 50 : -50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <Paper elevation={4} sx={{ borderRadius: 4, overflow: 'hidden', lineHeight: 0 }}>
              <img src={item.link} alt={item.title} style={{ width: '100%', height: 'auto', display: 'block' }} />
            </Paper>
          </motion.div>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>{item.title}</Typography>
          <Stack spacing={2}>
            {item.features.map((feat, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                <CheckCircle sx={{ color: 'success.main', fontSize: 24 }} />
                <Typography variant="body1" color="text.secondary">{feat}</Typography>
              </Box>
            ))}
          </Stack>
        </Grid2>
      </Grid2>
    ))}
  </Box>
);

const VideoShowcase = ({ payload }) => {
  const [activeTab, setActiveTab] = useState(0);
  return (
    <Box sx={{ py: 4 }}>
      <Grid2 container spacing={4}>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Stack spacing={2}>
            {payload.map((video, idx) => (
              <Card 
                key={idx} 
                component={motion.div}
                whileHover={{ scale: 1.02 }}
                onClick={() => setActiveTab(idx)}
                sx={{ 
                  cursor: 'pointer', borderLeft: 6, 
                  borderColor: activeTab === idx ? 'primary.main' : 'transparent',
                  bgcolor: activeTab === idx ? 'action.selected' : 'background.paper',
                }}
              >
                <CardContent>
                  <Typography variant="overline" color="primary" fontWeight={700}>{video.header}</Typography>
                  <Typography variant="h6">{video.title}</Typography>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 8 }}>
          <AnimatePresence mode="wait">
            <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <Paper elevation={12} sx={{ borderRadius: 4, overflow: 'hidden', bgcolor: 'black' }}>
                <video autoPlay muted loop playsInline src={payload[activeTab].link} style={{ width: '100%', display: 'block' }} />
              </Paper>
            </motion.div>
          </AnimatePresence>
        </Grid2>
      </Grid2>
    </Box>
  );
};

// --- NEW COMPONENT: AI AGENT SECTION ---
const AIAgentSection = () => {
  const theme = useTheme();
  
  return (
    <Box sx={{ 
      py: 10, px: 4, borderRadius: 8, 
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.05)} 0%, ${alpha(theme.palette.primary.light, 0.1)} 100%)`,
      border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.1),
      position: 'relative', overflow: 'hidden', mb: 15
    }}>
      {/* Decorative Background Icon */}
      <SmartToy sx={{ position: 'absolute', right: -20, bottom: -20, fontSize: 200, opacity: 0.05, transform: 'rotate(-15deg)' }} />

      <Grid2 container spacing={6} alignItems="center">
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Stack spacing={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip 
                label="UNDER DEVELOPMENT" 
                color="secondary" 
                size="small" 
                sx={{ fontWeight: 800, animation: 'pulse 2s infinite ease-in-out',
                  '@keyframes pulse': { '0%': { opacity: 1 }, '50%': { opacity: 0.5 }, '100%': { opacity: 1 } }
                }} 
              />
              <Typography variant="overline" sx={{ letterSpacing: 2, fontWeight: 700, color: 'primary.main' }}>
                Coming Soon
              </Typography>
            </Box>
            
            <Typography variant="h3" fontWeight={800}>
              AI Automation Agents
            </Typography>
            
            <Typography variant="h6" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            Deploy custom AI Automation Agents to streamline your administrative oversight. These virtual administrators handle routine tasks and ensure team accountability through automated workflows, while providing a comprehensive audit trail for transparent conflict resolution.
            </Typography>

            <Grid2 container spacing={2}>
              {[
                { icon: <NotificationsActive color="primary" />, text: "Automated session reminders for Tutors" },
                { icon: <HistoryEdu color="primary" />, text: "Assessment completion accountability" },
                { icon: <AutoMode color="primary" />, text: "Scheduled data exports for School Site Admins" },
              ].map((item, i) => (
                <Grid2 size={{ xs: 12 }} key={i}>
                  <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderRadius: 3, bgcolor: 'background.paper' }}>
                    {item.icon}
                    <Typography variant="body2" fontWeight={600}>{item.text}</Typography>
                  </Paper>
                </Grid2>
              ))}
            </Grid2>
          </Stack>
        </Grid2>
        
        <Grid2 size={{ xs: 12, md: 6 }}>
          <motion.div 
            animate={{ y: [0, -15, 0] }} 
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          >
            <Paper elevation={0} sx={{ 
              p: 4, borderRadius: 6, textAlign: 'center', 
              bgcolor: alpha(theme.palette.common.white, 0.6), 
              backdropFilter: 'blur(10px)', border: '1px solid white' 
            }}>
              <SmartToy sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" fontWeight={700} gutterBottom>Virtual Administrator</Typography>
              <Typography variant="body2" color="text.secondary">
                "An agent is a dedicated administrator that operates based on your specific organizational preferences."
              </Typography>
              <Button disabled sx={{ mt: 3, borderRadius: 4 }}>Beta Access Coming Soon</Button>
            </Paper>
          </motion.div>
        </Grid2>
      </Grid2>
    </Box>
  );
};

// --- MAIN HOMEPAGE ---

const HomePage = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box sx={{ pt: 15, pb: 10, textAlign: 'center', background: `radial-gradient(circle at 50% 0%, ${theme.palette.primary.light}15, transparent 50%)` }}>
        <Container maxWidth="md">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            <Typography variant="h2" fontWeight={900} sx={{ mb: 3 }}>Deliver educational services effortlessly</Typography>
            <Typography variant="h5" color="text.secondary" sx={{ mb: 5 }}>Easy monitoring, powerful analytics, and data logs within seconds.</Typography>
            <Button variant="contained" size="large" onClick={() => navigate('/register')} endIcon={<ArrowForward />} sx={{ px: 5, py: 2, borderRadius: 10, fontWeight: 700 }}>Get Started</Button>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Admin Dashboard Video */}
        <Box sx={{ mb: 15 }}>
          <Divider sx={{ mb: 8 }}><Chip label="Real-time Platform" color="info" /></Divider>
          <Paper elevation={0} sx={{ borderRadius: 6, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
            <video autoPlay muted loop playsInline src={admin_data.link} style={{ width: '100%' }} />
          </Paper>
        </Box>

        {/* Features Grid */}
        <Grid2 container spacing={3} sx={{ mb: 15 }}>
          {primaryFeatures.map((item, index) => (
            <Grid2 size={{ xs: 12, sm: 6, md: 3 }} key={index}>
              <Paper sx={{ p: 4, height: '100%', borderRadius: 4, transition: '0.3s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 } }}>
                <Box sx={{ color: `${item.color}.main`, mb: 2 }}>{item.icon}</Box>
                <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>{item.description}</Typography>
              </Paper>
            </Grid2>
          ))}
        </Grid2>

        <AuditComplianceSection />
        <br /> 

        <ValidationInsightsSection />       

        {/* Oversight Section */}
        <Divider sx={{ mb: 8 }}><Chip label="In-Depth Monitoring" color="info" /></Divider>
        <FeatureSlide payload={tutor_data_} />
        <FeatureSlide payload={student_data_} />

        {/* NEW: AI AGENT SECTION */}
        <Divider sx={{ my: 10 }}><Chip label="The Future of Automation" color="secondary" /></Divider>
        <AIAgentSection />

        {/* Video Showcase */}
        <Divider sx={{ my: 12 }}><Chip label="Sneak Peek" color="info" /></Divider>
        <VideoShowcase payload={video_data_} />

        {/* Organization Architecture */}
        <Divider sx={{ my: 12 }}><Chip label="Data Architecture" color="info" /></Divider>
        <Grid2 container spacing={8} alignItems="center" sx={{ mb: 15 }}>
          <Grid2 size={{ xs: 12, md: 7 }}><Paper elevation={8} sx={{ borderRadius: 4, overflow: 'hidden' }}><img src={organization_data.link} alt="Org" style={{ width: '100%' }} /></Paper></Grid2>
          <Grid2 size={{ xs: 12, md: 5 }}>
            <Typography variant="h3" fontWeight={800} gutterBottom>{organization_data.title}</Typography>
            <Stack spacing={3}>
              {organization_data.features.map((feat, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ minWidth: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', mt: 1.2 }} />
                  <Typography variant="body1" color="text.secondary">{feat}</Typography>
                </Box>
              ))}
            </Stack>
          </Grid2>
        </Grid2>
      </Container>
      <Footer />
    </Box>
  );
};

export default HomePage;